import React from 'react';
import { Search, Plus } from 'lucide-react';

const ProcesVerbalHeader = ({
  searchTerm,
  onSearchChange,
  onAddNew,
  filters,
  onFilterChange,
  soutenances,
  stats
}) => {
  const mentions = [
    { value: 'PASSABLE', label: 'Passable' },
    { value: 'ASSEZ_BIEN', label: 'Assez bien' },
    { value: 'BIEN', label: 'Bien' },
    { value: 'TRES_BIEN', label: 'Très bien' },
    { value: 'EXCELLENT', label: 'Excellent' }
  ];

  const decisions = [
    { value: 'ADMIS', label: 'Admis' },
    { value: 'ADMIS_RESERVE', label: 'Admis avec réserve' },
    { value: 'AJOURNE', label: 'Ajourné' },
    { value: 'REPORTE', label: 'Reporté' }
  ];

  const getSoutenanceLabel = (soutenance) => {
    if (!soutenance) return 'Non définie';
    
    let etudiantsNoms = '';
    if (soutenance.etudiants_soutenance && soutenance.etudiants_soutenance.length > 0) {
      const etudiant = soutenance.etudiants_soutenance[0].etudiant;
      etudiantsNoms = `${etudiant.nom} ${etudiant.prenom}`;
    } else if (soutenance.etudiant_principal_info) {
      etudiantsNoms = `${soutenance.etudiant_principal_info.nom} ${soutenance.etudiant_principal_info.prenom}`;
    }
    
    return etudiantsNoms || soutenance.titre_travail?.substring(0, 40) || 'Soutenance';
  };

  return (
    <div className="space-y-6">
      {/* Header avec recherche et bouton de création */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher un procès-verbal..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={onAddNew}
          className="inline-flex items-center px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium whitespace-nowrap"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nouveau PV
        </button>
      </div>

      {/* Statistiques */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
            <div className="text-sm text-gray-600">Total PV</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-green-600">{stats.valides}</div>
            <div className="text-sm text-gray-600">Validés</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-yellow-600">{stats.en_attente}</div>
            <div className="text-sm text-gray-600">En attente</div>
          </div>
          <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
            <div className="text-2xl font-bold text-blue-600">{stats.note_moyenne?.toFixed(1) || '0'}</div>
            <div className="text-sm text-gray-600">Note moyenne</div>
          </div>
        </div>
      )}

      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Soutenance
            </label>
            <select
              value={filters.soutenance}
              onChange={(e) => onFilterChange({...filters, soutenance: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les soutenances</option>
              {soutenances.map(soutenance => (
                <option key={soutenance.id} value={soutenance.id}>
                  {getSoutenanceLabel(soutenance)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mention
            </label>
            <select
              value={filters.mention}
              onChange={(e) => onFilterChange({...filters, mention: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les mentions</option>
              {mentions.map(mention => (
                <option key={mention.value} value={mention.value}>
                  {mention.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Décision
            </label>
            <select
              value={filters.decision}
              onChange={(e) => onFilterChange({...filters, decision: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Toutes les décisions</option>
              {decisions.map(decision => (
                <option key={decision.value} value={decision.value}>
                  {decision.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Statut
            </label>
            <select
              value={filters.valide}
              onChange={(e) => onFilterChange({...filters, valide: e.target.value})}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Tous les statuts</option>
              <option value="true">Validés</option>
              <option value="false">En attente</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => onFilterChange({soutenance: '', mention: '', decision: '', valide: ''})}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Réinitialiser
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcesVerbalHeader; 